import type { ComponentProps } from "react";
import { cn } from "../utils";

/**
 * Placeholder that holds the shape of the content still loading. Prefer it over
 * a centered spinner whenever the final layout is known: the content then gains
 * fidelity in place instead of appearing somewhere else on the screen.
 */
export function Skeleton({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      aria-hidden
      className={cn(
        "rounded-control bg-surface-sunken motion-safe:animate-pulse-soft",
        className,
      )}
      {...props}
    />
  );
}

/** Text line placeholder. `h-3.5` matches the 0.8125rem body line box. */
export function SkeletonText({
  className,
  ...props
}: ComponentProps<typeof Skeleton>) {
  return <Skeleton className={cn("h-3.5 w-full", className)} {...props} />;
}

/**
 * Rows of a table that has not arrived yet. Widths cycle so the block reads as
 * data instead of a grid of identical bars.
 */
export function SkeletonTable({
  rows = 8,
  columns = 5,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  const widths = ["w-full", "w-3/4", "w-1/2", "w-2/3", "w-5/6"];
  return (
    <div className={cn("divide-y divide-line", className)} aria-hidden>
      <div className="flex h-8 items-center gap-3 border-b border-line bg-surface-sunken px-3">
        {Array.from({ length: columns }, (_, column) => (
          <SkeletonText key={column} className="h-2 w-16 bg-surface" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-3 px-3 py-2">
          {Array.from({ length: columns }, (_, column) => (
            <SkeletonText
              key={column}
              className={cn(
                "flex-1",
                widths[(row + column) % widths.length],
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
